import React, { ReactElement, Ref, Validator, WeakValidationMap, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import LabelButton from '../LabelButton';
import useIsWrapping from '../useIsWrapping';
import { getBorderRadius } from '../utils/borderRadiusUtils';
import forwardRefToProps from '../utils/forwardRefToProps';
import { forgeClassHelper } from '../utils/classes';

const classes = forgeClassHelper('radio-button');

export type RadioButtonSize = 'small' | 'medium' | 'large';
export const radioButtonSizes: RadioButtonSize[] = ['small', 'medium', 'large'];

export type RadioButtonOption = {
  /** Optional accessible label applied to the underlying radio. By default, the label is used as the aria-label. But if label contains non-text markup such as an icon, this prop is useful for specifying a text-only accessible label. */
  ariaLabel?: string;

  /** Button label to display */
  label: React.ReactNode;

  /** Underlying button value passed to change handler when selected */
  value: string;
};

export interface RadioButtonProps {
  /** CSS class to apply to the root wrapper div */
  className?: string;

  /** When true, automatically resize the buttons to large when they stack because they can't fit on a single line */
  largeWhenStacked?: boolean;

  /** Unique name used for the underlying radio group. This name must be unique on the page. */
  name: string;

  /** A button renders for each option. Each `RadioButtonOption` has 2 properties: `label: React.ReactNode` and `value: string`.  */
  options: Array<RadioButtonOption>;

  /** This function is passed the selected button's value when it is clicked/selected */
  onChange: (value: string) => void;

  /** Radio button size. The large size is automatically applied when the buttons wrap. */
  size?: RadioButtonSize;

  /** Selected value */
  value: string;

  /** a ref to the outermost <div> */
  forwardedRef?: Ref<HTMLDivElement>;
}

/** Button group that acts as an alternative to radio group lists. */
function RadioButton({
  className,
  forwardedRef,
  largeWhenStacked = true,
  name,
  onChange,
  options,
  size = 'medium',
  value,
}: RadioButtonProps): ReactElement {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const ref = useRef<HTMLButtonElement[]>([]);
  const stacked = useIsWrapping(ref);

  /** Handles selecting a button via click, Space or Enter keys */
  const handleClick =
    (optionValue: string): (() => void) =>
    () => {
      if (value === optionValue) {
        // Clear out the value if an already-selected button is clicked
        onChange('');
      } else {
        onChange(optionValue);
      }
      return;
    };

  return (
    <div ref={forwardedRef} role="radiogroup" {...classes({ modifiers: { stacked, row: !stacked }, extra: className })}>
      {options.map((o, index) => {
        const isSelected = o.value === value;
        const isFocused = index === focusedIndex;

        return (
          <LabelButton
            key={o.value}
            ref={(el) => {
              if (el) ref.current[index] = el;
            }}
            // If stacked and largeWhenStacked is true, force large size, regardless of size setting.
            size={stacked && largeWhenStacked ? 'large' : size}
            focused={isFocused}
            active={isSelected}
            borderRadius={getBorderRadius(index, options.length, stacked)}
            {...classes({
              element: 'button',
              modifiers: { selected: isSelected, focused: isFocused, stacked, row: !stacked },
            })}
            onClick={handleClick(o.value)}
          >
            {o.label}
            <input
              tabIndex={-1}
              type="radio"
              readOnly
              name={name}
              className="fe_c_radio-button__radio"
              onFocus={() => setFocusedIndex(index)}
              onBlur={() => setFocusedIndex(null)}
              aria-checked={isSelected ? 'true' : 'false'}
              aria-label={typeof o.label === 'string' && !o.ariaLabel ? o.label : o.ariaLabel}
              checked={isSelected}
              value={o.value}
            />
          </LabelButton>
        );
      })}
    </div>
  );
}

const radioButtonPropTypes: WeakValidationMap<RadioButtonProps> = {
  /** CSS class applied to the root wrapper div */
  className: PropTypes.string,

  /** When true, automatically resize the buttons to large when they stack because they can't fit on a single line */
  largeWhenStacked: PropTypes.bool,

  /** Unique name used for the underlying radio group. This name must be unique on the page. */
  name: PropTypes.string.isRequired,

  /** A button renders for each option. Each `RadioButtonOption` has 2 properties: `label: React.ReactNode` and `value: string`.  */
  options: PropTypes.array.isRequired,

  /** Function called when a button is selected */
  onChange: PropTypes.func.isRequired,

  /** Radio button size. The large size is automatically applied when the buttons wrap. */
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /** Selected value */
  value: PropTypes.string.isRequired,

  /** a ref to the outermost <div> */
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func.isRequired,
    /** Note: { current: PropTypes.instanceOf(HTMLInputElement) } would be more
     * more correct, but causes a StackOverflow when building gatsby.
     */
    PropTypes.shape({ current: PropTypes.any }).isRequired,
  ]) as Validator<Ref<HTMLDivElement>>,
};

RadioButton.propTypes = radioButtonPropTypes;

export default forwardRefToProps(RadioButton);
